import {
  Box,
  Dialog,
  IconButton,
  Skeleton,
  Typography,
} from "@mui/material";
import HubIcon from "@mui/icons-material/Hub";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import { LeadsManagementData } from "../hooks/useLeadsManagementData";
import { BRAND_COLOR, BrandMatchBrandSummary, BrandMatchResult } from "../constants";

interface BrandMatchModalProps {
  data: LeadsManagementData;
}

// Loading-state skeleton counts mirror the mockup: 4 brand-card placeholders
// above one tall condition-table placeholder.
const BRAND_CARD_SKELETON_COUNT = 4;

function brandBadge(brand: BrandMatchBrandSummary): { label: string; color: string; bg: string } {
  if (brand.assigned) return { label: "Assigned brand", color: "#7B1FA2", bg: "#F3E5F5" };
  if (brand.eligible) return { label: "Eligible", color: "#2E7D32", bg: "#E8F5E9" };
  return { label: "Not eligible", color: "#C62828", bg: "#FDECEA" };
}

function matchResultColor(result: BrandMatchResult): string {
  if (result === "Match") return "#2E7D32";
  if (result === "No Match") return "#C62828";
  return "#9e9e9e";
}

export default function BrandMatchModal({ data }: BrandMatchModalProps) {
  const {
    matchModalOpen,
    matchLead,
    matchLoading,
    matchError,
    matchDiagnostic,
    closeBrandMatch,
    onMatchModalExited,
    rerunBrandMatch,
    canReportToBrand,
    reportToBrand,
    reportingBrandKey,
  } = data;

  const eligibleOtherBrands = matchDiagnostic
    ? matchDiagnostic.brands.filter((brand) => brand.eligible && !brand.assigned)
    : [];
  const hasEligibleOthers = eligibleOtherBrands.length > 0;
  const totalConditions = matchDiagnostic?.rows.length ?? 0;
  const brandColumnCount = matchDiagnostic?.brands.length ?? 0;

  return (
    <Dialog
      open={matchModalOpen}
      onClose={(_event, reason) => {
        // Only the header/footer close controls should dismiss this modal —
        // an accidental click outside or Escape shouldn't lose the query result.
        if (reason === "backdropClick" || reason === "escapeKeyDown") return;
        closeBrandMatch();
      }}
      TransitionProps={{ onExited: onMatchModalExited }}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2, maxHeight: "92vh" } }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.75, px: 3.25, py: 2.25, borderBottom: "1px solid #ececec" }}>
        <HubIcon sx={{ color: BRAND_COLOR, fontSize: 26, mt: 0.25 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 20, fontWeight: 700, color: "#1a1a1a" }}>
            Brand match diagnostic
          </Typography>
          {matchLead && (
            <Typography sx={{ fontSize: 13.5, color: "#757575", mt: 0.5 }}>
              Lead #{matchLead.id} · {matchLead.name} · assigned to {matchLead.brand}
            </Typography>
          )}
        </Box>
        <IconButton size="small" onClick={closeBrandMatch} title="Close">
          <CloseIcon fontSize="small" sx={{ color: "#9e9e9e" }} />
        </IconButton>
      </Box>

      <Box sx={{ flex: 1, overflowY: "auto", px: 3.25, py: 2.25, bgcolor: "#fafafa" }}>
        {matchLoading && (
          <Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                bgcolor: "#fbf7fd",
                border: "1px solid #f0e3f5",
                borderRadius: 2,
                px: 2,
                py: 1.75,
              }}
            >
              <Box
                sx={{
                  width: 19,
                  height: 19,
                  borderRadius: "50%",
                  border: "2.5px solid #ecdcf3",
                  borderTopColor: BRAND_COLOR,
                  "@keyframes brandMatchSpin": { to: { transform: "rotate(360deg)" } },
                  animation: "brandMatchSpin 0.8s linear infinite",
                }}
              />
              <Typography sx={{ fontSize: 13.5, color: "#6A1B9A" }}>
                Running brand match query for lead #{matchLead?.id}…
              </Typography>
              <Typography
                sx={{
                  ml: "auto",
                  fontSize: 12,
                  color: "#a98bb5",
                  fontFamily: "'Roboto Mono', monospace",
                }}
              >
                BQL · brand_match_diagnostic
              </Typography>
            </Box>

            <Box sx={{ display: "flex", gap: 1.75, mt: 2 }}>
              {Array.from({ length: BRAND_CARD_SKELETON_COUNT }).map((_, index) => (
                <Skeleton key={index} variant="rounded" sx={{ flex: 1, height: 132, bgcolor: "#f1eef3" }} />
              ))}
            </Box>
            <Skeleton variant="rounded" sx={{ height: 290, mt: 2.25, bgcolor: "#f1eef3" }} />
          </Box>
        )}

        {!matchLoading && matchError && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, bgcolor: "#FFEBEE", border: "1px solid #f5c6cb", borderRadius: 2, px: 2, py: 1.75 }}>
            <InfoOutlinedIcon sx={{ color: "#C62828", fontSize: 20 }} />
            <Typography sx={{ fontSize: 13.5, color: "#C62828", flex: 1 }}>
              {matchError}
            </Typography>
            <Typography
              onClick={rerunBrandMatch}
              sx={{ fontSize: 12.5, fontWeight: 500, color: BRAND_COLOR, cursor: "pointer", whiteSpace: "nowrap" }}
            >
              Re-run query
            </Typography>
          </Box>
        )}

        {!matchLoading && !matchError && matchDiagnostic && (
          <Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.125,
                borderRadius: 2,
                px: 1.75,
                py: 1.5,
                fontSize: 13.5,
                bgcolor: hasEligibleOthers ? "#F1F8F2" : "#fafafa",
                border: `1px solid ${hasEligibleOthers ? "#cde9d1" : "#ececec"}`,
                color: hasEligibleOthers ? "#1B5E20" : "#757575",
              }}
            >
              {hasEligibleOthers ? (
                <WorkspacePremiumIcon sx={{ fontSize: 20 }} />
              ) : (
                <InfoOutlinedIcon sx={{ fontSize: 20 }} />
              )}
              <Typography sx={{ fontSize: 13.5 }}>
                {hasEligibleOthers
                  ? `This lead meets every requirement of ${eligibleOtherBrands.length} additional brand${eligibleOtherBrands.length > 1 ? "s" : ""} beyond ${matchLead?.brand ?? "its assigned brand"}.`
                  : `No additional brand accepts this lead on all ${totalConditions} condition${totalConditions === 1 ? "" : "s"}.`}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", gap: 1.75, mt: 2 }}>
              {matchDiagnostic.brands.map((brand) => {
                const badge = brandBadge(brand);
                const barPercent = brand.totalConditions
                  ? (brand.matchedCount / brand.totalConditions) * 100
                  : 0;
                const canReport = brand.eligible && !brand.assigned && canReportToBrand;
                const isReportingThisBrand = reportingBrandKey === brand.key;
                const reportDisabled = reportingBrandKey !== null && !isReportingThisBrand;
                return (
                  <Box
                    key={brand.key}
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      borderRadius: 1.25,
                      p: "14px 15px",
                      border: `1px solid ${brand.eligible ? "#cde9d1" : "#ececec"}`,
                      bgcolor: brand.eligible ? "#F7FBF7" : "#fff",
                    }}
                  >
                    <Typography sx={{ fontSize: 14, fontWeight: 500, color: "#212121" }}>
                      {brand.name}
                    </Typography>
                    <Box sx={{ mt: 1 }}>
                      <Box
                        component="span"
                        sx={{
                          display: "inline-block",
                          px: 1.375,
                          py: 0.375,
                          borderRadius: 6,
                          fontSize: 12,
                          fontWeight: 500,
                          bgcolor: badge.bg,
                          color: badge.color,
                        }}
                      >
                        {badge.label}
                      </Box>
                    </Box>
                    <Box sx={{ height: 5, borderRadius: 3, bgcolor: "#f0f0f2", mt: 1.375, overflow: "hidden" }}>
                      <Box
                        sx={{
                          height: "100%",
                          borderRadius: 3,
                          width: `${barPercent}%`,
                          bgcolor: brand.eligible ? "#2E7D32" : "#C62828",
                        }}
                      />
                    </Box>
                    <Typography sx={{ fontSize: 12, color: "#757575", mt: 0.875 }}>
                      {brand.matchedCount} of {brand.totalConditions} conditions met
                    </Typography>
                    {canReport && (
                      <Typography
                        onClick={() => !reportDisabled && !isReportingThisBrand && reportToBrand(brand.key)}
                        data-tracker="1037"
                        sx={{
                          fontSize: 12.5,
                          fontWeight: 500,
                          color: reportDisabled ? "#c0a6cc" : BRAND_COLOR,
                          cursor: reportDisabled || isReportingThisBrand ? "default" : "pointer",
                          mt: 1.375,
                        }}
                      >
                        {isReportingThisBrand ? "Reporting…" : "Report to this brand →"}
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Box>

            <Box sx={{ bgcolor: "#fff", border: "1px solid #ececec", borderRadius: 1.25, mt: 2.25, overflowX: "auto" }}>
              <Box sx={{ minWidth: 1000 }}>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: `190px 150px repeat(${brandColumnCount}, minmax(140px, 1fr))`,
                    borderBottom: "1px solid #ececec",
                    bgcolor: "#fcfbfd",
                  }}
                >
                  <Box sx={{ px: 1.75, py: 1.25, fontSize: 12, fontWeight: 700, color: "#212121" }}>
                    Condition
                  </Box>
                  <Box sx={{ px: 1.5, py: 1.25, fontSize: 12, fontWeight: 700, color: "#212121", borderLeft: "1px solid #f2f2f2" }}>
                    Lead answer
                  </Box>
                  {matchDiagnostic.brands.map((brand) => (
                    <Box
                      key={brand.key}
                      sx={{
                        px: 1.5,
                        py: 1.25,
                        fontSize: 12,
                        fontWeight: 700,
                        color: brand.assigned ? "#7B1FA2" : "#212121",
                        borderLeft: "1px solid #f2f2f2",
                      }}
                    >
                      {brand.name}
                    </Box>
                  ))}
                </Box>
                {matchDiagnostic.rows.map((row) => (
                  <Box
                    key={row.key}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: `190px 150px repeat(${brandColumnCount}, minmax(140px, 1fr))`,
                      borderBottom: "1px solid #f4f4f4",
                    }}
                  >
                    <Box sx={{ px: 1.75, py: 1.375 }}>
                      <Typography sx={{ fontSize: 13.5, fontWeight: 500, color: "#212121" }}>
                        {row.nice}
                      </Typography>
                      <Typography sx={{ fontSize: 11.5, color: "#a5a5a5", fontFamily: "'Roboto Mono', monospace" }}>
                        {row.key}
                      </Typography>
                    </Box>
                    <Box sx={{ px: 1.5, py: 1.375, borderLeft: "1px solid #f2f2f2", fontSize: 13.5, color: "#424242" }}>
                      {row.leadAnswer}
                    </Box>
                    {row.cells.map((cell) => (
                      <Box
                        key={cell.brandKey}
                        sx={{
                          px: 1.5,
                          py: 1.375,
                          borderLeft: "1px solid #f2f2f2",
                          bgcolor: cell.matchResult === "No Match" ? "#FDECEA" : "transparent",
                        }}
                      >
                        <Typography sx={{ fontSize: 13, color: "#616161" }}>{cell.requirement}</Typography>
                        <Typography
                          sx={{
                            display: "inline-block",
                            mt: 0.5,
                            fontSize: 11.5,
                            fontWeight: 700,
                            letterSpacing: "0.3px",
                            color: matchResultColor(cell.matchResult),
                          }}
                        >
                          {cell.matchResult}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                ))}
              </Box>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", mt: 1.5 }}>
              <Typography
                onClick={rerunBrandMatch}
                sx={{ ml: "auto", fontSize: 12.5, fontWeight: 500, color: BRAND_COLOR, cursor: "pointer" }}
              >
                Re-run query
              </Typography>
            </Box>
          </Box>
        )}
      </Box>

      <Box sx={{ display: "flex", justifyContent: "flex-end", px: 3.25, py: 1.75, borderTop: "1px solid #ececec" }}>
        <Box
          component="button"
          onClick={closeBrandMatch}
          sx={{
            border: "none",
            bgcolor: BRAND_COLOR,
            color: "#fff",
            fontFamily: "'Roboto', sans-serif",
            fontSize: 14,
            fontWeight: 500,
            letterSpacing: "0.4px",
            px: 3,
            py: 1.375,
            borderRadius: 2,
            cursor: "pointer",
            "&:hover": { bgcolor: "#7B1FA2" },
          }}
        >
          Close
        </Box>
      </Box>
    </Dialog>
  );
}
